/**
 * True when React Hook Form's `dirtyFields` holds at least one `true` leaf.
 *
 * Aggregate `formState.isDirty` and "any key present" both over-report: RHF
 * keeps reverted leaves as `false` and leaves empty nested objects behind, and
 * `isDirty` compares every value to `defaultValues`, so a programmatic
 * `setValue` prefill after mount reads as an edit. Only a `true` leaf means the
 * user changed something. `ignoreKeys` skips keys at any depth (shadow fields
 * with no registered input that RHF still flags).
 */
/**
 * Shadow fields of `InvoiceLineItems` lines: no registered input, but RHF
 * flags them in dirtyFields when the org-default tax fill / scan hints write
 * them with `shouldDirty: false`. Every real user tax change also dirties
 * `tax_rate` (or another field), so skipping them loses nothing. Shared by
 * every form that embeds `InvoiceLineItems` (expense, AP bill).
 */
export const INVOICE_LINE_SHADOW_FIELDS: ReadonlySet<string> = new Set([
  'tax_rate_id',
  'tax_rate_unset',
  'tax_rate_hint',
  'tax_rate_hint_computed',
  'tax_rate_hint_vat',
  'tax_rate_hint_vat_base',
  'tax_rate_hint_net',
  'tax_rate_hint_gross',
  'tax_rate_hint_amount_kept',
  'tax_rate_confirmed',
]);

/** `hasDirtyLeaf` over a form whose lines are `InvoiceLineItems` lines. */
export const hasUserLineDirtyLeaf = (flags: unknown): boolean =>
  hasDirtyLeaf(flags, INVOICE_LINE_SHADOW_FIELDS);

export function hasDirtyLeaf(flags: unknown, ignoreKeys?: ReadonlySet<string>): boolean {
  if (flags === true) return true;
  if (!flags || typeof flags !== 'object') return false;
  return Object.entries(flags).some(
    ([key, child]) => !ignoreKeys?.has(key) && hasDirtyLeaf(child, ignoreKeys),
  );
}
